import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";
import { useAuth } from "./AuthContext";
import { Input, Button, FormCard } from "./FormComponents";
import { useToast } from "./hooks/useToast";
import { PasswordInput } from "./components/PasswordInput";

function friendlyError(code?: string): string {
  switch (code) {
    case "auth/invalid-email":
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    default:
      return "An unexpected error occurred. Please try again.";
  }
}

export default function SignIn() {
  const navigate = useNavigate();
  const { currentUser, isLoading: isAuthLoading } = useAuth();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isAuthLoading && currentUser) {
      navigate("/albums");
    }
  }, [currentUser, isAuthLoading, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // The useEffect above will handle the redirect on successful login
    } catch (err) {
      const errorCode = (err as { code?: string })?.code;
      toast.error(friendlyError(errorCode));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard title="Log In">
      {/* Sign Up Link */}
      <p className="text-center text-base text-gray-700">
        New to this site?{" "}
        <Link to="/signup" className="font-normal text-primary-600 hover:text-primary-700 underline">
          Sign Up
        </Link>
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '1rem' }}>
        <Input
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          disabled={isLoading}
          variant="underline"
        />
        <PasswordInput
          id="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          disabled={isLoading}
        />

        {/* Forgot Password Link */}
        <Link to="/passwordreset" className="text-primary-600 hover:text-primary-700 underline text-base" style={{ marginTop: '-1rem' }}>
          Forgot password?
        </Link>

        <Button type="submit" disabled={isLoading} variant="brown">
          {isLoading ? "Signing in..." : "Log In"}
        </Button>
      </form>
    </FormCard>
  );
}

